import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useIsMobile from "../hooks/useIsMobile";
import Reveal from "../components/common/Reveal";
import SectionTag from "../components/common/SectionTag";
import Icon from "../components/common/Icon";
import Seo from "../components/common/Seo";
import { BRAND, SCRIPT_URL, INTERNSHIP_TRACKS } from "../data/constants";
import { colors, fonts, emphasis, sectionPad, maxW, springHover, cellHover, iconPop } from "../styles/theme";

const HIGHLIGHTS = [["Award", "Certificate"], ["Briefcase", "Real Projects"], ["Users", "Expert Mentors"], ["Calendar", "Flexible Duration"]];

const inputStyle = {
  width: "100%", background: colors.paper, border: `1px solid ${colors.borderStrong}`, borderRadius: 3,
  padding: "12px 15px", color: colors.ink, fontFamily: fonts.body, fontSize: 14, outline: "none",
};
const labelStyle = { display: "block", fontFamily: fonts.body, fontSize: 12.5, fontWeight: 600, color: colors.inkDim, marginBottom: 7 };

export default function InternshipPage() {
  const mob = useIsMobile();
  const [form, setForm] = useState({ name: "", email: "", track: "", college: "", msg: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.name || !form.email || !form.track) {
      setError("Please fill in Name, Email and Track.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await fetch(SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, type: "Internship Application" }),
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74 }}>
      <Seo
        title="Internship Program 2026 | Charith"
        description="Charith's internship program: real client projects, expert mentors, and a certificate — across Frontend, Backend, Full Stack, Cloud, Mobile, and UI/UX tracks."
        path="/internship"
      />
      <section style={{ background: colors.paper, ...sectionPad(mob) }}>
        <Reveal style={{ ...maxW, maxWidth: 720 }}>
          <SectionTag>Internship Program · 2026</SectionTag>
          <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 32 : "clamp(30px,4.2vw,54px)", lineHeight: 1.12, color: colors.ink, letterSpacing: "-.01em", marginBottom: 20 }}>
            Launch your career with <span style={emphasis}>real experience</span>
          </h1>
          <p style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: mob ? 15 : 17, lineHeight: 1.8, maxWidth: 520, marginBottom: 32 }}>
            Real client projects from day one. No busywork — just mentorship, skills, and a portfolio that gets you hired.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr 1fr" : "repeat(4,auto)", gap: mob ? 18 : 34 }}>
            {HIGHLIGHTS.map(([icon, label], i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Icon name={icon} size={17} style={{ color: colors.rust }} />
                <span style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: 13.5 }}>{label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section style={{ background: colors.paper, padding: mob ? "0 6% 56px" : "0 6% 96px" }}>
        <div style={{ ...maxW }}>
          <Reveal style={{ marginBottom: mob ? 30 : 44 }}>
            <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 26 : 34, color: colors.ink }}>Choose your track</h2>
            <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 13.5, marginTop: 9 }}>6 specialised tracks designed around industry demand.</p>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "repeat(3,1fr)", gap: 0, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, overflow: "hidden" }}>
            {INTERNSHIP_TRACKS.map((t, i) => {
              const rightBorder = !mob && (i % 3 !== 2);
              const topBorder = i > (mob ? 0 : 2);
              return (
                <Reveal key={i} delay={i * 0.05} style={{ borderRight: rightBorder ? `1px solid ${colors.borderStrong}` : "none", borderTop: topBorder ? `1px solid ${colors.borderStrong}` : "none" }}>
                  <motion.div initial="rest" whileHover="hover" variants={cellHover} transition={springHover} style={{ padding: mob ? 22 : 26, height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                      <motion.div variants={iconPop} transition={springHover} style={{ display: "inline-flex" }}>
                        <Icon name={t.icon} size={22} style={{ color: colors.rust }} />
                      </motion.div>
                      <span style={{ border: `1px solid ${colors.borderStrong}`, color: colors.inkDim, fontFamily: fonts.body, fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 20 }}>{t.duration}</span>
                    </div>
                    <h3 style={{ fontFamily: fonts.body, fontWeight: 700, fontSize: 15.5, color: colors.ink, marginBottom: 13 }}>{t.track}</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {t.skills.map((sk, j) => (
                        <span key={j} style={{ border: `1px solid ${colors.border}`, color: colors.inkFaint, fontFamily: fonts.body, fontSize: 11, padding: "3px 9px", borderRadius: 3 }}>{sk}</span>
                      ))}
                    </div>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ background: colors.paperAlt, borderTop: `1px solid ${colors.border}`, ...sectionPad(mob) }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <Reveal style={{ textAlign: "center", marginBottom: 38 }}>
            <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 26 : 34, color: colors.ink }}>Apply now</h2>
            <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 13.5, marginTop: 10 }}>
              Or email us:{" "}
              <button onClick={() => window.open(`mailto:${BRAND.email}`, "_self")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: colors.rust, fontFamily: fonts.body, fontSize: 13.5, fontWeight: 600, textDecoration: "underline" }}>{BRAND.email}</button>
            </p>
          </Reveal>

          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div key="sent" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center", background: colors.paper, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, padding: mob ? 32 : 48 }}>
                <Icon name="CheckCircle2" size={40} style={{ color: colors.olive, marginBottom: 16 }} />
                <h3 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 21, color: colors.ink, marginBottom: 10 }}>Application submitted</h3>
                <p style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: 14 }}>We'll reach out to {form.email} within 2–3 business days.</p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ background: colors.paper, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, padding: mob ? 22 : 38 }}>
                {[
                  { label: "Full Name *", key: "name", type: "text", ph: "Your full name" },
                  { label: "Email Address *", key: "email", type: "email", ph: BRAND.email },
                  { label: "College / University", key: "college", type: "text", ph: "Your institution" },
                ].map((f) => (
                  <div key={f.key} style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>{f.label}</label>
                    <input type={f.type} placeholder={f.ph} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} style={inputStyle} />
                  </div>
                ))}
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Preferred Track *</label>
                  <select value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} style={{ ...inputStyle, color: form.track ? colors.ink : colors.inkFainter }}>
                    <option value="">Select a track…</option>
                    {INTERNSHIP_TRACKS.map((t) => <option key={t.track} value={t.track}>{t.track}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 26 }}>
                  <label style={labelStyle}>Why do you want to join?</label>
                  <textarea placeholder="Tell us about yourself and your goals…" value={form.msg} onChange={(e) => setForm({ ...form, msg: e.target.value })}
                    rows={4} style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                {error && <p style={{ color: colors.rust, fontFamily: fonts.body, fontSize: 13, marginBottom: 16 }}>{error}</p>}
                <motion.button
                  whileHover={{ backgroundColor: loading ? colors.ink : colors.rust }}
                  onClick={submit}
                  disabled={loading}
                  style={{ width: "100%", background: colors.ink, border: "none", color: colors.cream, fontFamily: fonts.body, fontWeight: 700, fontSize: 15, padding: 16, borderRadius: 3, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Submitting…" : "Submit Application"}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
